// client/src/components/admin/league-tabs/DivisionManagementTab.js
import React from 'react'
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  DraggablePlayer,
  DraggableTeam,
  DroppableDivision,
} from '../DnDComponents'
import {
  DIVISION_ASSIGNMENT_EDIT_ALLOWED_STATUSES,
  getDivisionCreationRestrictedMessage,
  getDivisionAssignmentRestrictedMessage,
  canCreateDivisions,
} from '../../../constants/leagueStatuses'

/**
 * DivisionManagementTab - Manages division creation, player assignments, and drag-and-drop
 *
 * @param {object} props
 * @param {object} props.league - Current league object
 * @param {array} props.divisions - Array of division objects
 * @param {array} props.players - Array of player objects
 * @param {object} props.assignments - Object mapping division_id to array of player_ids
 * @param {object} props.newDivision - Form state for creating new division
 * @param {function} props.onNewDivisionChange - Handler for new division form changes
 * @param {function} props.onCreateDivision - Handler for creating a new division
 * @param {function} props.onDeleteDivision - Handler for deleting a division
 * @param {function} props.onUpdateDivisionName - Handler for updating division name
 * @param {function} props.onSaveAssignments - Handler for saving draft assignments
 * @param {function} props.onDragEnd - Handler for drag-and-drop events
 * @param {function} props.onRemovePlayer - Handler for removing a player from the league
 * @param {function} props.onDecouplePartner - Handler for decoupling a doubles partner
 */
export default function DivisionManagementTab({
  league,
  divisions,
  players,
  assignments,
  newDivision,
  onNewDivisionChange,
  onCreateDivision,
  onDeleteDivision,
  onUpdateDivisionName,
  onSaveAssignments,
  onDragEnd,
  onRemovePlayer,
  onDecouplePartner,
}) {
  // Dnd-kit sensors for pointer and touch
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor))

  const isDoubles = league?.league_type?.toLowerCase() === 'doubles'

  // Group partnered players into pairs for doubles leagues.
  // Only pairs players who are both in the same playerList (same division).
  // Split-division partners render as solo cards with partner badges.
  const groupDoublesPlayers = (playerList) => {
    const pairs = []
    const solos = []
    const seen = new Set()
    for (const p of playerList) {
      if (seen.has(p.id)) continue
      seen.add(p.id)
      if (p.partner) {
        // Only pair with partner if they are in the same list (same division)
        const localPartner = playerList.find(
          (pp) => pp.id === p.partner.partner_player_id,
        )
        if (localPartner && !seen.has(localPartner.id)) {
          seen.add(localPartner.id)
          pairs.push({ teamId: p.partner.team_id, players: [p, localPartner] })
        } else if (!localPartner) {
          // Partner is in a different division — show as solo with partner badge
          solos.push(p)
        } else {
          // localPartner found but already seen (already paired from their side)
          // skip — they're already in a pair
        }
      } else {
        solos.push(p)
      }
    }
    return { pairs, solos }
  }

  // Render player items — grouped team cards for doubles, individual cards otherwise
  const renderPlayerItems = (playerList) => {
    if (!isDoubles) {
      return playerList.map((p) => (
        <DraggablePlayer
          key={p.id}
          player={p}
          disabled={!canEdit}
          canRemove={canRemovePlayer(league)}
          onRemove={onRemovePlayer}
        />
      ))
    }
    const { pairs, solos } = groupDoublesPlayers(playerList)
    return (
      <>
        {pairs.map(({ teamId, players: teamPlayers }) => (
          <DraggableTeam
            key={`team-${teamId}-${teamPlayers[0].id}`}
            teamId={teamId}
            primaryPlayerId={teamPlayers[0].id}
            players={teamPlayers}
            disabled={!canEdit}
            onDecouplePartner={onDecouplePartner}
          />
        ))}
        {solos.map((p) => (
          <DraggablePlayer
            key={p.id}
            player={p}
            disabled={!canEdit}
            canRemove={canRemovePlayer(league)}
            onRemove={onRemovePlayer}
            isDoubles={true}
            onDecouplePartner={p.partner ? onDecouplePartner : null}
          />
        ))}
      </>
    )
  }

  // Check permissions
  const canCreate = league && canCreateDivisions(league.league_status)
  const canEdit =
    league &&
    DIVISION_ASSIGNMENT_EDIT_ALLOWED_STATUSES.includes(league.league_status)

  // Get unassigned players
  const getUnassigned = () => {
    const assigned = Object.values(assignments).flat()
    return players.filter((p) => !assigned.includes(p.id))
  }

  // Check if player can be removed based on league status
  const canRemovePlayer = (league) => {
    if (!league) return false
    const prePublishStatuses = [
      'draft',
      'registration_open',
      'registration_closed',
    ]
    return prePublishStatuses.includes(league.league_status)
  }

  // Render locked message when assignments cannot be edited
  const renderDivisionAssignmentsLockedMessage = (status) => (
    <div className="alert alert-warning mb-3" role="alert">
      <i className="fas fa-lock me-2" aria-hidden="true"></i>
      <strong>{getDivisionAssignmentRestrictedMessage(status)}</strong>
    </div>
  )

  return (
    <>
      {/* Division Creation Form */}
      <div className="my-3">
        <h4>Create Division</h4>
        {!canCreate && (
          <div className="alert alert-info py-2 small mb-3" role="alert">
            {getDivisionCreationRestrictedMessage(league.league_status)}
          </div>
        )}
        <input
          name="division_name"
          value={newDivision.division_name}
          onChange={onNewDivisionChange}
          placeholder="Division Name"
          className="form-control form-control-sm mb-2"
          disabled={!canCreate}
          aria-label="Division name"
        />
        <select
          name="rank_level"
          value={newDivision.rank_level}
          onChange={onNewDivisionChange}
          className="form-control form-control-sm mb-2"
          disabled={!canCreate}
          aria-label="Rank level"
        >
          <option value="">Select Rank</option>
          <option value="2.5">2.5</option>
          <option value="3.0">3.0</option>
          <option value="3.5">3.5</option>
          <option value="4.0">4.0</option>
          <option value="4.5">4.5</option>
          <option value="5.0">5.0</option>
        </select>
        <select
          name="gender"
          value={newDivision.gender}
          onChange={onNewDivisionChange}
          className="form-control form-control-sm mb-2"
          disabled={!canCreate}
          aria-label="Gender"
        >
          <option value="">Select Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="coed">Co-ed</option>
        </select>
        <button
          className="btn btn-success"
          onClick={onCreateDivision}
          data-track="admin.add_division"
          disabled={!canCreate}
          aria-label="Add division"
        >
          Add Division
        </button>
      </div>

      {/* Division Assignments */}
      <h4>Division Assignments</h4>

      {!canEdit && league
        ? renderDivisionAssignmentsLockedMessage(league.league_status)
        : canEdit && (
            <p className="text-muted mb-3">
              <i className="fas fa-hand-paper me-2" aria-hidden="true"></i>
              Drag and drop players between divisions to organize your league.
            </p>
          )}

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="row">
          {/* Unassigned Players Pool */}
          <DroppableDivision
            id="unassigned"
            label="Unassigned"
            disabled={!canEdit}
            aria-disabled={!canEdit}
          >
            {renderPlayerItems(getUnassigned())}
          </DroppableDivision>

          {/* Division Cards */}
          {divisions.map((d) => (
            <DroppableDivision
              key={d.id}
              id={d.id}
              label={d.division_name}
              canDelete={(assignments[d.id] || []).length === 0 && canEdit}
              onDelete={() => onDeleteDivision(d.id)}
              canEdit={true}
              onEditName={(newName) => onUpdateDivisionName(d.id, newName)}
              disabled={!canEdit}
              aria-disabled={!canEdit}
            >
              {renderPlayerItems(
                (assignments[d.id] || [])
                  .map((pid) => players.find((p) => p.id === pid))
                  .filter(Boolean),
              )}
            </DroppableDivision>
          ))}
        </div>
      </DndContext>

      {/* Save Button */}
      <div className="my-3">
        <button
          className="btn btn-primary"
          onClick={onSaveAssignments}
          disabled={!canEdit}
          aria-disabled={!canEdit}
          title={
            !canEdit && league
              ? `Changes not allowed in status '${league.league_status}'`
              : undefined
          }
          data-track="admin.save_draft"
        >
          {canEdit ? 'Save Draft' : 'Changes Not Allowed'}
        </button>
      </div>
    </>
  )
}
